import { Trans, useTranslation } from "react-i18next";
import { Accordion, Button, Text, TextInput } from "@ucl/react-library";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DOBInput } from "src/components/dob-input/dob-input";
import { useBoundInput } from "src/hooks/use-bound-input";
import { AuthorisationFormValues } from "src/types/form-data";
import { getValidationSchema } from "src/pages/authorisation-page/authorisation-validations";
import referenceNumberLocation from "src/assets/reference-location.svg";
import { translationMapping } from "src/utils/translation-mappings";
import { useCase } from "src/context/case-context";
import { fetchCustomerDetails } from "src/api/services/customer-service";
import { InlineAlertMessage } from "src/components/inline-alert-message/inline-alert-message";
import bgImage from "../../assets/Maintenance_Background.png";
import { ErrorResponseCodes } from "src/consts/errorCodes";
import fordCreditImage from "src/assets/fordCreditLogoBlue.svg";

function AuthorisationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setCustomerData, setAuthorisedCase } = useCase();

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState({ title: "", body: <></> });

  const validationSchema = useMemo(() => getValidationSchema(t), [t]);
  const translationComponents = useMemo(() => translationMapping(t), [t]);

  const methods = useForm<AuthorisationFormValues>({
    defaultValues: {
      referenceNumber: "",
      dobDay: "",
      dobMonth: "",
      dobYear: "",
    },
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
  });

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = methods;

  const referenceNumberProps = useBoundInput({
    name: "referenceNumber",
    control,
  });

  const sanitiseRefInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.value = event.target.value.replace(/\s+/g, "").toUpperCase();
    referenceNumberProps.onChange(event);
  };

  const onSubmit = async (formData: AuthorisationFormValues) => {
    setShowError(false);

    try {
      const data = await fetchCustomerDetails(formData);

      setAuthorisedCase(data, formData.referenceNumber.trim());

      navigate("/case");
    } catch (error: any) {
      const attemptsRemaining = error.data?.attemptsRemaining;
      const errorCode: ErrorResponseCodes = error.data?.errorCode;
      let errorType = "signInGeneric";

      if (errorCode === ErrorResponseCodes.LOCKED_REF) {
        errorType = "refLock";
      } else if (errorCode === ErrorResponseCodes.LOCKED_SESSION) {
        errorType = "sessionLock";
      } else if (errorCode === ErrorResponseCodes.AUTH_FAILURE) {
        if (attemptsRemaining > 1) {
          errorType = "signInFailed";
        } else {
          errorType = "lastAttempt";
        }
      }

      setShowError(true);
      setErrorMessage({
        title: t(`errors.${errorType}.title`),
        body: (
          <Trans
            i18nKey={`errors.${errorType}.body`}
            count={attemptsRemaining}
            values={{ attemptsRemaining }}
            components={translationComponents}
          />
        ),
      });
    }
  };

  return (
    <div
      className="w-screen min-h-screen bg-cover bg-center bg-no-repeat overflow-auto"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="flex flex-col items-center justify-center pt-10 px-5 pb-20 self-stretch">
        <div className="text-white">
          <img src={fordCreditImage} alt="Ford Credit Logo" className="w-52" data-testid="fordLogo"/>
        </div>

        <div className="flex flex-col items-center gap-5 px-5 py-[30px] md:p-10 bg-white rounded-2xl max-w-[540px]">
          <Text.H1 fontWeight="semi-bold">{t("mfcTitle")}</Text.H1>

          <Text.P fontWeight="semi-bold" size="large">
            {t("landingPage.subtitle")}
          </Text.P>
          <Text.P size={"large"}>
            {t("landingPage.moreThanOneAgreement")}
          </Text.P>

          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 w-full">
              <TextInput
                data-testid="referenceNumber"
                labelProp={{
                  label: t("landingPage.referenceNumber"),
                }}
                placeholder=""
                {...referenceNumberProps}
                onChange={sanitiseRefInput}
              />

              <Accordion>
                <Accordion.Item id="1" title={t("landingPage.findReferenceNumber")} titleSize="small">
                  <Accordion.Panel className="flex flex-col gap-5">
                    <Text.P size="large">{t("landingPage.findRefParagraph1")}</Text.P>
                    <Text.P size="large">{t("landingPage.findRefParagraph2")}</Text.P>
                    <Text.P size="large">
                      <Trans i18nKey="landingPage.findRefParagraph3" components={translationComponents} />
                    </Text.P>
                    <img data-testid="referenceNumberLocation" src={referenceNumberLocation} alt="Reference Number Location" />
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
              <DOBInput t={t} control={control} errors={errors} />
              {showError && (
                <InlineAlertMessage
                  title={errorMessage.title}
                  body={errorMessage.body}
                  variant="error"
                  onClose={() => {
                    setShowError(false);
                  }}
                />
              )}

              <Button type="submit" disabled={isSubmitting}>
                {t("landingPage.continue")}
              </Button>
            </form>
          </FormProvider>

          <div className="flex flex-col gap-5 p-5 bg-gray-100 rounded-xl">
            <Text.P size="large" fontWeight="semi-bold">
              {t("landingPage.aboutScheme")}
            </Text.P>
            <Text.P size="large">{t("landingPage.schemeInfo1")}</Text.P>
            <Text.P size="large">{t("landingPage.schemeInfo2")}</Text.P>
            <Text.P size="large">
              <Trans i18nKey="landingPage.schemeInfo3" components={translationComponents} />
            </Text.P>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthorisationPage;
